import React, { createContext, useState, useEffect, useMemo } from "react";
import {
  addFeatureFlagsStorage,
  getFeatureFlagsStorage
} from "../utils/localStorage/featureFlagsStorage";

type FeatureFlagContextType = {
  featureFlags: FeatureFlag[];
  setFeatureFlags: CallableFunction;
  toggleFlag: CallableFunction;
  isFlagEnabled: (status: string) => boolean;
};

type UserContextProps = {
  children: React.ReactNode;
};

export type FeatureFlag = {
  key: string;
  title: string;
  description?: string;
  isEnabled: boolean;
};

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  featureFlags: [],
  setFeatureFlags: () => true,
  toggleFlag: () => true,
  isFlagEnabled: () => true
});

export const FeatureFlagContextProvider = ({ children }: UserContextProps) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);

  /**
   * Set feature flags on load
   */
  useEffect(() => {
    (async () => {
      const storedFeatureFlags = await getFeatureFlagsStorage();

      const initialFeatureFlags = storedFeatureFlags.map((featureFlag: any) => {
        const storedVersion =
          storedFeatureFlags &&
          storedFeatureFlags.find((flag: any) => featureFlag.key === flag.key);
        return storedVersion
          ? { ...featureFlag, isEnabled: storedVersion.isEnabled }
          : featureFlag;
      });

      setFeatureFlags(initialFeatureFlags);
    })();
  }, []);

  /**
   * Update storage when they change
   */
  useEffect(() => {
    addFeatureFlagsStorage(featureFlags);
  }, [featureFlags]);

  /**
   * Set a new value of a flag
   *
   * @param flagKey
   * @param newIsEnabled
   */
  const toggleFlag = (flagKey: string, newIsEnabled: boolean) => {
    setFeatureFlags(
      featureFlags.map((featureFlag: FeatureFlag) =>
        featureFlag.key === flagKey
          ? { ...featureFlag, isEnabled: newIsEnabled }
          : featureFlag
      )
    );
  };

  /**
   * Check if a flag is enabled
   */
  const isFlagEnabled = (flagKey: string) =>
    !!featureFlags.find(
      (featureFlag: FeatureFlag) =>
        featureFlag.key === flagKey && featureFlag.isEnabled
    );

  const contextValue = useMemo(
    () => ({
      featureFlags,
      setFeatureFlags,
      toggleFlag,
      isFlagEnabled
    }),
    [featureFlags]
  );

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to use the FeatureFlag context
 */
export const useFeatureFlagContext = () => {
  const context = React.useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagContextProvider"
    );
  }
  return context;
};
